/**
 * Helper centralisé pour sanitiser du HTML avant rendu via
 * `dangerouslySetInnerHTML`. Utilise DOMPurify avec une whitelist
 * conservative qui couvre les besoins des templates de conventions,
 * pages CMS, descriptions de modèles et previews Word importés.
 *
 * Usage :
 *   import { sanitizeHtml } from '../utils/sanitizeHtml';
 *   <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(htmlString) }} />
 *
 * SAFE_HTML_OPTIONS expose la config par défaut, qu'on peut surcharger
 * au cas par cas si un composant a besoin de balises supplémentaires.
 */
import DOMPurify from 'dompurify';

/**
 * Whitelist permissive adaptée aux templates riches (conventions ESS,
 * documents officiels). Autorise mise en forme + tableaux + images
 * + liens, mais bloque tout script / iframe / event handler.
 */
const SAFE_HTML_OPTIONS = {
  ALLOWED_TAGS: [
    // Structure
    'div', 'span', 'section', 'article', 'header', 'footer', 'aside',
    'main', 'nav', 'figure', 'figcaption',
    // Texte
    'p', 'br', 'hr', 'strong', 'em', 'b', 'i', 'u', 's', 'small',
    'sub', 'sup', 'mark', 'blockquote', 'pre', 'code',
    // Titres
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    // Listes
    'ul', 'ol', 'li', 'dl', 'dt', 'dd',
    // Tableaux
    'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption',
    'colgroup', 'col',
    // Médias & liens
    'a', 'img',
    // Layout
    'style',
  ],
  ALLOWED_ATTR: [
    'class', 'id', 'style', 'href', 'target', 'rel', 'title', 'alt',
    'src', 'width', 'height', 'colspan', 'rowspan', 'align', 'valign',
    'cellpadding', 'cellspacing', 'border', 'data-testid',
  ],
  // Empêche `javascript:`, `data:`, etc. dans href/src
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|ftp):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  // Force rel="noopener noreferrer" sur les liens target="_blank"
  ADD_ATTR: ['target'],
  // Ne permet PAS les éléments dangereux
  FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
  FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  KEEP_CONTENT: true,
};

/**
 * Sanitise une chaîne HTML pour rendu safe via dangerouslySetInnerHTML.
 * Retourne une chaîne vide si l'input est null/undefined.
 */
export function sanitizeHtml(html, options = {}) {
  if (html === null || html === undefined) return '';
  if (typeof html !== 'string') {
    try {
      html = String(html);
    } catch {
      return '';
    }
  }
  return DOMPurify.sanitize(html, { ...SAFE_HTML_OPTIONS, ...options });
}

/**
 * Variante stricte (texte + mise en forme simple uniquement, pas d'images
 * ni de liens externes). Utile pour les commentaires utilisateurs ou
 * les champs courts.
 */
export function sanitizeHtmlStrict(html) {
  return sanitizeHtml(html, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'b', 'i', 'u', 'span', 'ul', 'ol', 'li'],
    ALLOWED_ATTR: ['class'],
  });
}

// Garantit `rel="noopener noreferrer"` sur les liens target="_blank"
// (protection contre tabnabbing dans les conventions/pages CMS).
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

export default sanitizeHtml;
